import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined } from '@ant-design/icons';
import { useInspections } from '../../hooks/useQuality';
import type { InspectionRecord } from '../../services/quality.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

type ReceivingRow = InspectionRecord & {
  grn?: { id: string; grn_number: string; grn_status: string; inspection_status: string };
};

function grnLabel(row: ReceivingRow): string {
  if (row.grn?.grn_number) return row.grn.grn_number;
  const obs = String(row.observations || '');
  const m = obs.match(/GRN\s+([A-Z0-9-]+)/i);
  return m?.[1] || '—';
}

const ReceivingInspectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [resultFilter, setResultFilter] = useState<string>('');

  const { data: inspections = [], isLoading } = useInspections({
    inspection_category: 'Receiving',
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(resultFilter ? { result: resultFilter } : {}),
  });

  const rows = inspections as ReceivingRow[];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      Scheduled: 'blue',
      'In Progress': 'processing',
      Completed: 'success',
      Failed: 'error',
      'On Hold': 'warning',
      Cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getResultColor = (result: string) => {
    const colors: Record<string, string> = {
      Pass: 'success',
      Fail: 'error',
      Conditional: 'warning',
      Pending: 'default',
    };
    return colors[result] || 'default';
  };

  const filtered = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter((i) => {
      const grn = grnLabel(i).toLowerCase();
      return (
        i.inspection_number.toLowerCase().includes(q) ||
        grn.includes(q) ||
        String(i.inspection_subcategory || '')
          .toLowerCase()
          .includes(q) ||
        String(i.inspector_name || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [rows, searchText]);

  const stats = useMemo(() => {
    const pending = rows.filter(
      (i) => i.status === 'Scheduled' || i.status === 'Pending Approval'
    ).length;
    const inProgress = rows.filter((i) => i.status === 'In Progress').length;
    const completed = rows.filter((i) => i.status === 'Completed').length;
    return { total: rows.length, pending, inProgress, completed };
  }, [rows]);

  const columns: ColumnsType<ReceivingRow> = [
    {
      title: 'Inspection No.',
      dataIndex: 'inspection_number',
      key: 'inspection_number',
      width: 140,
    },
    {
      title: 'GRN',
      key: 'grn',
      width: 180,
      render: (_, row) => grnLabel(row),
    },
    {
      title: 'Category',
      dataIndex: 'inspection_subcategory',
      key: 'subcategory',
      width: 180,
      render: (v: string) => v || 'Receiving',
    },
    {
      title: 'Date',
      key: 'date',
      width: 130,
      render: (_, row) => {
        const d = row.scheduled_date || row.actual_date || row.created_at;
        return d ? dayjs(d).format('DD/MM/YYYY') : '—';
      },
      sorter: (a, b) =>
        new Date(a.scheduled_date || a.created_at || 0).getTime() -
        new Date(b.scheduled_date || b.created_at || 0).getTime(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: (result: string) => <Tag color={getResultColor(result)}>{result || 'Pending'}</Tag>,
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector_name',
      key: 'inspector',
      width: 140,
      render: (v: string) => v || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, row) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => navigate(`/scm/procurement/receiving-inspections/${row.id}`)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 4 }}>
        Receiving Inspections
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          GRN critical-item QC — separate from stage / line inspections
          (all GRNs now require Receiving QC; critical is tagged on lines)
      </Text>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total" value={stats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="In Progress" value={stats.inProgress} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Completed" value={stats.completed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder="Search inspection no., GRN, inspector..."
            allowClear
            style={{ width: 320 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || '')}
          >
            <Option value="Scheduled">Scheduled</Option>
            <Option value="In Progress">In Progress</Option>
            <Option value="Completed">Completed</Option>
            <Option value="On Hold">On Hold</Option>
            <Option value="Cancelled">Cancelled</Option>
          </Select>
          <Select
            placeholder="Result"
            allowClear
            style={{ width: 140 }}
            value={resultFilter || undefined}
            onChange={(v) => setResultFilter(v || '')}
          >
            <Option value="Pending">Pending</Option>
            <Option value="Pass">Pass</Option>
            <Option value="Fail">Fail</Option>
            <Option value="Conditional">Conditional</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 1100 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>
    </div>
  );
};

export default ReceivingInspectionsPage;
